import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetMemoriesResponse,
  MemoryItem,
  AgentEvents,
  MemoryUpdatedEvent,
} from '@dreamai/types';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import { ReactComponent as MemoryIcon } from '../../icons/nav-links/memory.svg';
import { ReactComponent as VisibleIcon } from '../../icons/visible.svg';
import { ReactComponent as NotVisibleIcon } from '../../icons/not-visible.svg';
import MemoryCard from './MemoryCard';
import MemoryEmptyState from './MemoryEmptyState';
import { OnboardingModal } from '../onboarding/OnboardingModal';
import './memory-area.scss';

type TabType = 'unconfirmed' | 'aboutYou' | 'organisation' | 'relationships';

const MemoryArea: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<MemoryItem[]>([]);
  const [counts, setCounts] = useState<GetMemoriesResponse['counts'] | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<TabType>('unconfirmed');

  // Filters for unconfirmed tab
  const [filterAboutYou, setFilterAboutYou] = useState(true);
  const [filterOrganisation, setFilterOrganisation] = useState(true);
  const [filterRelationships, setFilterRelationships] = useState(true);

  const fetchMemories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetMemoriesResponse>('/memories', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setItems(response.data.items);
        setCounts(response.data.counts);

        // Default to unconfirmed tab if there are unconfirmed items
        if (response.data.counts.unconfirmed > 0) {
          setActiveTab('unconfirmed');
        } else if (response.data.counts.aboutYou > 0) {
          setActiveTab('aboutYou');
        } else if (response.data.counts.organisation > 0) {
          setActiveTab('organisation');
        } else if (response.data.counts.relationships > 0) {
          setActiveTab('relationships');
        }
      } else {
        setError('Failed to load memories');
      }
    } catch (err) {
      console.error('Error fetching memories:', err);
      setError('Failed to load memories');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  // Initial fetch
  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  // Listen for Pusher events for memory updates
  useEffect(() => {
    if (!pusherChannel) return;

    const handleMemoryUpdated = (data: MemoryUpdatedEvent) => {
      fetchMemories();
    };

    pusherChannel.bind(AgentEvents.MEMORY_UPDATED, handleMemoryUpdated);

    return () => {
      pusherChannel.unbind(AgentEvents.MEMORY_UPDATED, handleMemoryUpdated);
    };
  }, [pusherChannel, fetchMemories]);

  const handleApprove = async (item: MemoryItem) => {
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/memories/approve',
        { id: item.id, type: item.type },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // Refresh will happen via Pusher
    } catch (err) {
      console.error('Error approving memory:', err);
    }
  };

  const handleDecline = async (item: MemoryItem, correction: string) => {
    // Optimistically update the item to show "Rewriting..." state
    setItems((prevItems) =>
      prevItems.map((i) =>
        i.id === item.id
          ? {
              ...i,
              raw: { ...i.raw, pendingCorrection: true },
            }
          : i,
      ),
    );

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/memories/decline',
        { id: item.id, type: item.type, correction },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // Refresh will happen via Pusher
    } catch (err) {
      console.error('Error declining memory:', err);
      // Revert optimistic update on error
      setItems((prevItems) =>
        prevItems.map((i) =>
          i.id === item.id
            ? {
                ...i,
                raw: { ...i.raw, pendingCorrection: false },
              }
            : i,
        ),
      );
    }
  };

  // Filter items based on active tab and filters
  const getFilteredItems = (): MemoryItem[] => {
    if (activeTab === 'unconfirmed') {
      return items.filter((item) => {
        if (item.approved) return false;
        if (item.scope === 'aboutYou' && !filterAboutYou) return false;
        if (item.scope === 'organisation' && !filterOrganisation) return false;
        if (item.scope === 'relationships' && !filterRelationships)
          return false;
        return true;
      });
    }

    return items.filter((item) => item.scope === activeTab && item.approved);
  };

  const filteredItems = getFilteredItems();

  if (loading) {
    return (
      <div className='memory-area'>
        <div className='loading'>Loading memories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='memory-area'>
        <div className='error'>{error}</div>
      </div>
    );
  }

  return (
    <div className='memory-area inset-white-backed'>
      <OnboardingModal
        modalKey='memory_page_intro'
        title='Your Dreamies remember!'
        imageSrc='/onboarding-modals/memory-page-intro.png'
        primaryButtonText='Explore my memories'
      >
        <p>
          Dream Hub shows you everything that your Dreamies remember. You can
          edit those memories or give further information.
        </p>
        <p>
          Memories mean your Dreamies can remember more. Carrying the
          information across conversations without them needing to look it up.
        </p>
        <p>
          Dreamies learn about the people you know, the products you offer and
          everything else to do with your company. They learn actively (you can
          tell them to remember) and passively (they'll figure it out
          themselves).
        </p>
        <p>
          You'll need to approve things they've learned passively, and can make
          any changes you'd like.
        </p>
      </OnboardingModal>

      <div className='header'>
        <div className='header-icon'>
          <MemoryIcon />
        </div>
        <div className='header-text'>
          <h2>Dream Hub</h2>
          <p>Where your dreamies keep their memories.</p>
        </div>
      </div>

      <div className='tabs'>
        <div
          className={`tab ${activeTab === 'unconfirmed' ? 'active' : ''}`}
          onClick={() => setActiveTab('unconfirmed')}
        >
          Unconfirmed memories{' '}
          <span className='count'>{counts?.unconfirmed || 0}</span>
        </div>
        <div
          className={`tab ${activeTab === 'aboutYou' ? 'active' : ''}`}
          onClick={() => setActiveTab('aboutYou')}
        >
          About you{' '}
          <span className='count'>{counts?.aboutYouApproved || 0}</span>
        </div>
        <div
          className={`tab ${activeTab === 'organisation' ? 'active' : ''}`}
          onClick={() => setActiveTab('organisation')}
        >
          Your organisation{' '}
          <span className='count'>{counts?.organisationApproved || 0}</span>
        </div>
        <div
          className={`tab ${activeTab === 'relationships' ? 'active' : ''}`}
          onClick={() => setActiveTab('relationships')}
        >
          People you know{' '}
          <span className='count'>{counts?.relationshipsApproved || 0}</span>
        </div>
      </div>

      <div className='content'>
        <div className='content-inner'>
          {activeTab === 'unconfirmed' && counts && counts.unconfirmed > 0 && (
            <>
              <p className='explanation'>
                Until they're confirmed, your dreamies won't use the memory or
                recognise the relationship.
              </p>

              <h2 className='section-title'>Your team wants you to confirm</h2>

              <div className='filters'>
                <button
                  className={`filter-btn ${filterAboutYou ? 'active' : ''}`}
                  onClick={() => setFilterAboutYou(!filterAboutYou)}
                >
                  <span className='count'>
                    {counts?.aboutYouUnconfirmed || 0}
                  </span>
                  About you
                  {filterAboutYou ? <VisibleIcon /> : <NotVisibleIcon />}
                </button>
                <button
                  className={`filter-btn ${filterOrganisation ? 'active' : ''}`}
                  onClick={() => setFilterOrganisation(!filterOrganisation)}
                >
                  <span className='count'>
                    {counts?.organisationUnconfirmed || 0}
                  </span>
                  Your organisation
                  {filterOrganisation ? <VisibleIcon /> : <NotVisibleIcon />}
                </button>
                <button
                  className={`filter-btn ${filterRelationships ? 'active' : ''}`}
                  onClick={() => setFilterRelationships(!filterRelationships)}
                >
                  <span className='count'>
                    {counts?.relationshipsUnconfirmed || 0}
                  </span>
                  People you know
                  {filterRelationships ? <VisibleIcon /> : <NotVisibleIcon />}
                </button>
              </div>
            </>
          )}

          {filteredItems.length === 0 ? (
            <MemoryEmptyState variant={activeTab} />
          ) : (
            <div className='items'>
              {filteredItems.map((item) => (
                <MemoryCard
                  key={item.id}
                  item={item}
                  onApprove={handleApprove}
                  onDecline={handleDecline}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoryArea;
